import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getLabData } from '../services/labService';
import { getMemberLabData } from '../services/memberLabService';
import MainHeader from '../Components/MainHeader';

const ComparisonContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e1e5e9;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
`;

const Tab = styled.div`
  padding: 12px 5px;
  text-align: center;
  font-size: 13px;
  font-weight: bold;
  color: ${props => props.active ? 'white' : '#B0B0B0'};
  background-color: ${props => props.active ? '#00BBD3' : '#F2F2F2'};
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.2;
  white-space: nowrap;

  ${props => {
    if (props.children === 'สุขภาพของคุณ') {
      return `width: 152px;`;
    } else if (props.children === 'ผลตรวจรวม') {
      return `width: 120px;`;
    } else if (props.children === 'ผลตรวจเปรียบเทียบ') {
      return `width: 168px;`;
    }
    return '';
  }}

  &:hover {
    background-color: ${props => props.active ? '#00BBD3' : '#e9ecef'};
  }
`;

const Content = styled.div`
  padding: 24px;
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;
`;

const ComparisonTable = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  width: 100%;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
`;

const TableContent = styled.div`
  min-width: fit-content;
`;

const TableHeader = styled.div`
  background-color: #00BBD3;
  color: white;
`;

const HeaderRow = styled.div`
  display: grid;
`;

const HeaderCell = styled.div`
  padding: 8px 4px;
  font-size: 11px;
  font-weight: bold;
  text-align: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  word-break: break-word;
  white-space: nowrap;
  overflow: hidden;

  &:first-child {
    text-align: left;
    justify-content: flex-start;
    padding-left: 12px;
    white-space: normal;
  }

  &:last-child {
    border-right: none;
  }
`;

const TableBody = styled.div`
  background-color: white;
`;

const CategoryRow = styled.div`
  display: grid;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const CategoryCell = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  font-weight: bold;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  text-align: left;
  grid-column: 1 / -1;
`;

const TableRow = styled.div`
  display: grid;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 8px 4px;
  font-size: 13px;
  color: #333333;
  font-family: 'Noto Sans Thai', sans-serif !important;
  text-align: center;
  border-right: 1px solid #e0e0e0;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:last-child {
    border-right: none;
  }
`;

const TestNameCell = styled(TableCell)`
  justify-content: flex-start;
  text-align: left;
  padding-left: ${props => props.indent ? '24px' : '12px'};
  padding-right: 8px;
  color: #333333;
  font-size: 12px;
  background-color: #ffffff;
`;

const ComparisonResultsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testData, setTestData] = useState([]);
  const [dates, setDates] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // ====== ลองดึงข้อมูลจาก MFU API ก่อน (เส้นเดียวกับ mfu-admin) ======
        let labData = [];

        try {
          const memberLabData = await getMemberLabData();
          console.log('📊 Member Lab Data:', memberLabData);

          if (memberLabData?.labData) {
            // แปลงข้อมูลจาก memberLabData ให้อยู่ในรูปแบบที่ prepareComparisonData ใช้
            const formattedData = [];
            const allLabData = memberLabData.labData;

            Object.keys(allLabData).forEach(paramName => {
              const paramHistory = allLabData[paramName];
              if (Array.isArray(paramHistory)) {
                paramHistory.forEach(item => {
                  formattedData.push({
                    dateadd: item.date,
                    name: paramName,
                    value: item.value
                  });
                });
              }
            });

            if (formattedData.length > 0) {
              labData = formattedData;
            }
          }
        } catch (memberLabError) {
          console.warn("⚠️ Could not load member lab data:", memberLabError);
        }

        // Fallback ไป API เดิมถ้าไม่มีข้อมูล
        if (labData.length === 0) {
          const labResponse = await getLabData('', 1);
          labData = labResponse?.data || labResponse?.result || [];
        }

        const comparisonData = prepareComparisonData(labData);

        setTestData(comparisonData.testData);
        setDates(comparisonData.dates);

      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const prepareComparisonData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return { testData: [], dates: [] };
    }

    // Group by date
    const groupedByDate = {};
    data.forEach(item => {
      const date = item.dateadd ? item.dateadd.split(' ')[0] : '';
      if (!date) return;

      if (!groupedByDate[date]) {
        groupedByDate[date] = {};
      }

      groupedByDate[date][item.name] = item.value;
    });

    // Get all dates sorted (oldest first → newest last)
    const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
      new Date(a) - new Date(b)
    );

    const dates = sortedDates.map(d => formatThaiDate(d));
    const dateDataArray = sortedDates.map(d => groupedByDate[d]);

    // Helper function to get value for each date - รองรับหลายชื่อฟิลด์
    const getValues = (...fieldNames) => {
      return dateDataArray.map(data => {
        for (const fieldName of fieldNames) {
          if (data[fieldName] && data[fieldName] !== '-') {
            return data[fieldName];
          }
        }
        return '-';
      });
    };

    // Build comprehensive test data with ALL parameters
    const testData = [];

    // รายการตรวจ (Vital Signs)
    testData.push({ type: 'category', name: 'รายการตรวจ' });
    testData.push({ name: 'ความดันโลหิต (Systolic)', values: getValues('systolic'), unit: 'มม.ปรอท', indent: true });
    testData.push({ name: 'ความดันโลหิต (Diastolic)', values: getValues('diastolic'), unit: 'มม.ปรอท', indent: true });
    testData.push({ name: 'ชีพจร', values: getValues('pulse'), unit: 'ครั้ง/นาที', indent: true });
    testData.push({ name: 'อัตราการหายใจ', values: getValues('respiratory_rate', 'respiratoryrate'), unit: 'ครั้ง/นาที', indent: true });
    testData.push({ name: 'น้ำหนัก', values: getValues('weight'), unit: 'กก.', indent: true });
    testData.push({ name: 'ส่วนสูง', values: getValues('height'), unit: 'ซม.', indent: true });
    testData.push({ name: 'BMI', values: getValues('bmi'), unit: 'กก./ม.²', indent: true });

    // ความสมบูรณ์ของเม็ดเลือด (CBC)
    testData.push({ type: 'category', name: 'ความสมบูรณ์ของเม็ดเลือด' });
    testData.push({ name: 'เม็ดเลือดขาว (WBC)', values: getValues('wbc'), unit: 'cells/µL', indent: true });
    testData.push({ name: 'เม็ดเลือดแดง (RBC)', values: getValues('rbc'), unit: 'cells/µL', indent: true });
    testData.push({ name: 'ฮีโมโกลบิน (Hb)', values: getValues('hemoglobin', 'hb'), unit: 'g/dL', indent: true });
    testData.push({ name: 'ฮีมาโตคริต (Hct)', values: getValues('hematocrit', 'hct'), unit: '%', indent: true });
    testData.push({ name: 'เกล็ดเลือด (Platelet)', values: getValues('platelet', 'plt'), unit: 'cells/µL', indent: true });
    testData.push({ name: 'MCV', values: getValues('mcv'), unit: 'fL', indent: true });
    testData.push({ name: 'MCH', values: getValues('mch'), unit: 'pg/cell', indent: true });
    testData.push({ name: 'MCHC', values: getValues('mchc'), unit: 'g/dL', indent: true });
    testData.push({ name: 'นิวโทรฟิล (Neutrophil)', values: getValues('neutrophile'), unit: '%', indent: true });
    testData.push({ name: 'ลิมโฟไซต์ (Lymphocyte)', values: getValues('lymphocyte-count', 'lymphocyte'), unit: '%', indent: true });
    testData.push({ name: 'โมโนไซต์ (Monocyte)', values: getValues('monocyte-count', 'monocyte'), unit: '%', indent: true });
    testData.push({ name: 'เบโซฟิล (Basophil)', values: getValues('basophil-count', 'basophil'), unit: '%', indent: true });

    // น้ำตาลในเลือด
    testData.push({ type: 'category', name: 'น้ำตาลในเลือด' });
    testData.push({ name: 'น้ำตาลในเลือด (FBS)', values: getValues('sugar', 'fbs'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'HbA1C', values: getValues('hba1c'), unit: '%', indent: true });

    // ไขมันในเลือด
    testData.push({ type: 'category', name: 'ไขมันในเลือด' });
    testData.push({ name: 'คอเลสเตอรอลรวม', values: getValues('cholesterol'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'HDL (ไขมันดี)', values: getValues('hdl'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'LDL (ไขมันไม่ดี)', values: getValues('ldl'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'ไตรกลีเซอไรด์', values: getValues('triglyceride', 'tg'), unit: 'mg/dL', indent: true });

    // การทำงานของไต
    testData.push({ type: 'category', name: 'การทำงานของไต' });
    testData.push({ name: 'Creatinine', values: getValues('creatinine'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'BUN', values: getValues('bun'), unit: 'mg/dL', indent: true });
    testData.push({ name: 'eGFR', values: getValues('egfr'), unit: 'mL/min/1.73m²', indent: true });

    // การทำงานของตับ
    testData.push({ type: 'category', name: 'การทำงานของตับ' });
    testData.push({ name: 'SGOT (AST)', values: getValues('sgot', 'ast'), unit: 'U/L', indent: true });
    testData.push({ name: 'SGPT (ALT)', values: getValues('sgpt', 'alt'), unit: 'U/L', indent: true });
    testData.push({ name: 'ALP', values: getValues('alp'), unit: 'U/L', indent: true });

    // กรดยูริค
    testData.push({ type: 'category', name: 'กรดยูริค' });
    testData.push({ name: 'Uric Acid', values: getValues('uric_acid', 'uric'), unit: 'mg/dL', indent: true });

    // ผลการตรวจปัสสาวะ
    testData.push({ type: 'category', name: 'ผลการตรวจปัสสาวะ' });
    testData.push({ name: 'สีของปัสสาวะ', values: getValues('urinalysis_color'), unit: '', indent: true });
    testData.push({ name: 'ลักษณะปัสสาวะ', values: getValues('urinalysis_appearance'), unit: '', indent: true });
    testData.push({ name: 'pH', values: getValues('urinalysis_ph'), unit: '', indent: true });
    testData.push({ name: 'ความถ่วงจำเพาะ', values: getValues('urinalysis_specific_gravity'), unit: '', indent: true });
    testData.push({ name: 'น้ำตาล', values: getValues('urinalysis_glucose'), unit: '', indent: true });
    testData.push({ name: 'โปรตีน', values: getValues('urinalysis_protein'), unit: '', indent: true });
    testData.push({ name: 'คีโตน', values: getValues('urinalysis_ketone'), unit: '', indent: true });
    testData.push({ name: 'เลือด', values: getValues('urinalysis_blood'), unit: '', indent: true });
    testData.push({ name: 'เม็ดเลือดขาว', values: getValues('urinalysis_wbc'), unit: 'cells/HPF', indent: true });
    testData.push({ name: 'เม็ดเลือดแดง', values: getValues('urinalysis_rbc'), unit: 'cells/HPF', indent: true });
    testData.push({ name: 'Leukocyte', values: getValues('urinalysisleukocyte'), unit: '', indent: true });
    testData.push({ name: 'Nitrite', values: getValues('urinalysisnitrite'), unit: '', indent: true });
    testData.push({ name: 'Appearance', values: getValues('urinalysisapperancer'), unit: '', indent: true });
    testData.push({ name: 'Bilirubin', values: getValues('urinalysisbilirubin'), unit: '', indent: true });

    // ผลการตรวจอุจจาระ
    testData.push({ type: 'category', name: 'ผลการตรวจอุจจาระ' });
    testData.push({ name: 'สีของอุจจาระ', values: getValues('stool_color_result'), unit: '', indent: true });
    testData.push({ name: 'เม็ดเลือดแดง', values: getValues('stool_rbc_result'), unit: '', indent: true });
    testData.push({ name: 'เม็ดเลือดขาว', values: getValues('stool_wbc_result'), unit: '', indent: true });

    return { testData, dates };
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '-';

    // แยกเฉพาะส่วนวันที่ ถ้ามี timestamp
    const datePart = dateString.split('T')[0].split(' ')[0];

    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return dateString; // ถ้า format ไม่ถูกต้องให้ return กลับไป

    const thaiYear = parseInt(year) + 543;
    const shortYear = thaiYear.toString().slice(-2);
    return `${day}/${month}/${shortYear}`;
  };

  const handleTabClick = (tab) => {
    if (tab === 'สุขภาพของคุณ') {
      window.location.href = '/dashboard';
    } else if (tab === 'ผลตรวจรวม') {
      window.location.href = '/test-results';
    }
  };

  // Calculate grid columns based on number of dates
  const gridColumns = `140px repeat(${dates.length}, 75px)`;

  console.log('📅 Total dates:', dates.length);
  console.log('📅 Dates array:', dates);
  console.log('📏 Grid columns:', gridColumns);

  return (
    <ComparisonContainer>
      <MainHeader />

      <TabContainer>
        <Tab onClick={() => handleTabClick('สุขภาพของคุณ')}>สุขภาพของคุณ</Tab>
        <Tab onClick={() => handleTabClick('ผลตรวจรวม')}>ผลตรวจรวม</Tab>
        <Tab active>ผลตรวจเปรียบเทียบ</Tab>
      </TabContainer>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : testData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลสำหรับเปรียบเทียบ
          </div>
        ) : (
          <ComparisonTable>
            <TableWrapper>
              <TableContent>
                <TableHeader>
                  <HeaderRow style={{ gridTemplateColumns: gridColumns }}>
                    <HeaderCell>รายการตรวจ</HeaderCell>
                    {dates.map((date, index) => (
                      <HeaderCell key={index}>{date}</HeaderCell>
                    ))}
                  </HeaderRow>
                </TableHeader>

                <TableBody>
                  {testData.map((item, index) =>
                    item.type === 'category' ? (
                      <CategoryRow key={index} style={{ gridTemplateColumns: gridColumns }}>
                        <CategoryCell>{item.name}</CategoryCell>
                      </CategoryRow>
                    ) : (
                      <TableRow key={index} style={{ gridTemplateColumns: gridColumns }}>
                        <TestNameCell indent={item.indent}>
                          {item.name}
                        </TestNameCell>
                        {item.values.map((value, vIndex) => (
                          <TableCell key={vIndex}>
                            {value} {value !== '-' && item.unit}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  )}
                </TableBody>
              </TableContent>
            </TableWrapper>
          </ComparisonTable>
        )}
      </Content>
    </ComparisonContainer>
  );
};

export default ComparisonResultsPage;
